import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';
import clsx from 'clsx';
import { Grid, InputAdornment, TextField, Typography, FormHelperText } from '@material-ui/core';

// UTILITY
import { trimCharacters } from '@/utility/common';

// COMPONENTS
import RadioCard from '@/components/Form/RadioCard';
import AddressAutoComplete from '@/components/Form/AddressAutoComplete';

// STYLES
import { TitleContainer, Title } from '../styles';

const ZipCode = props => {
  const {
    t,
    values,
    handleChange,
    handleBlur,
    setFieldValue,
    setFieldTouched,
    touched,
    errors,
    counties,
    showCountyStep,
    onRadioCardChange,
    skippedAddressStep,
    internetOption,
    onAddressSelect,
    partner,
    analyticsLocation,
    isRequired,
  } = props;

  useEffect(() => {
    if (values.zipcode) {
      setFieldTouched('zipcode', true, true);
    }
  }, []);

  const getSubStepName = () => {
    if (internetOption === false && values.zipcode && !skippedAddressStep) {
      return 'address';
    }

    if (showCountyStep) {
      return 'counties';
    }

    return 'zipcode';
  };

  const subStepName = getSubStepName();
  const textOverwrites = partner?.text_overwrites || {};

  const handleCountyChange = event => {
    onRadioCardChange(event, handleChange);
  };

  const handleChangeZipCode = event => {
    setFieldValue('zipcode', trimCharacters(event.target.value));
  };

  const handleAddressChange = data => {
    onAddressSelect(data);
  };

  const getContent = name => {
    if (name === 'address') {
      return (
        <AddressAutoComplete
          onAddressChange={handleAddressChange}
          inputProps={{ autoFocus: true }}
          analyticsLocation={analyticsLocation}
        />
      );
    }

    if (name === 'counties') {
      return (
        <Grid container spacing={1} justifyContent="center">
          {counties.map(item => (
            <Grid item xs={6} sm={6} md={3} key={item.county_id}>
              <RadioCard
                name="county_id_fk"
                text={item.county_name}
                handleChange={handleCountyChange}
                handleBlur={handleBlur}
                value={item.county_id.toString()}
                checkedValue={values.county_id_fk}
                dataAttributes={{ 'data-dma_id_fk': item.dma_id_fk.toString() }}
              />
            </Grid>
          ))}
        </Grid>
      );
    }

    return (
      <>
        <TextField
          label={textOverwrites.zipcode_question_input || t(`inputs.zipcode`)}
          InputProps={{
            endAdornment: (
              <InputAdornment
                className={Boolean(!errors.zipcode) && !!values.zipcode ? 'success' : ''}
                position="start"
              >
                <i className="icon-check" />
              </InputAdornment>
            ),
          }}
          type="tel"
          name="zipcode"
          value={values.zipcode}
          onChange={handleChangeZipCode}
          onBlur={handleBlur}
          error={touched.zipcode && !!errors.zipcode}
          helperText={touched.zipcode && errors.zipcode}
          className="zipcodeinput"
        />
        <FormHelperText>{textOverwrites.zipcode_disclaimer}</FormHelperText>
      </>
    );
  };

  const getCopies = () => {
    let title;
    let subtitle;

    switch (subStepName) {
      case 'zipcode':
        title = textOverwrites.zipcode_question_title || t(`start.${subStepName}.title`);
        subtitle = textOverwrites.zipcode_question_subtitle || t(`start.${subStepName}.subtitle`);
        break;

      case 'address':
        title = textOverwrites.address_question_title || t(`start.${subStepName}.title`);
        break;
      default:
        title = t(`start.${subStepName}.title`);
        subtitle = t(`start.${subStepName}.subtitle`);
        break;
    }

    return {
      title,
      subtitle,
    };
  };

  return (
    <>
      <TitleContainer className="form-title zipcode-title">
        <Title
          className={clsx(
            subStepName === 'zipcode' && textOverwrites.fmb_zip_extra_details && 'main-title',
            subStepName === 'zipcode' &&
              !textOverwrites.fmb_zip_extra_details &&
              isRequired &&
              'required',
          )}
          variant="h4"
          align="center"
          component="h1"
        >
          {getCopies().title}
          {subStepName === 'address' && <span>{t('start.address.optional')}</span>}
        </Title>
        <Typography variant="subtitle2" align="center">
          {getCopies().subtitle}
        </Typography>
        {subStepName === 'zipcode' && !!textOverwrites.zipcode_second_title && (
          <Title
            className={clsx('title-second', isRequired && 'required')}
            variant="h4"
            align="center"
            component="h2"
          >
            {textOverwrites.zipcode_second_title}
          </Title>
        )}
      </TitleContainer>
      {getContent(subStepName)}
    </>
  );
};

ZipCode.defaultPros = {
  isRequired: false,
};

ZipCode.propTypes = {
  t: PropTypes.func.isRequired,
  values: PropTypes.object.isRequired,
  handleChange: PropTypes.func.isRequired,
  handleBlur: PropTypes.func.isRequired,
  setFieldValue: PropTypes.func.isRequired,
  setFieldTouched: PropTypes.func.isRequired,
  errors: PropTypes.object.isRequired,
  touched: PropTypes.object.isRequired,
  counties: PropTypes.oneOfType([PropTypes.array, PropTypes.oneOf([null])]),
  showCountyStep: PropTypes.bool.isRequired,
  onRadioCardChange: PropTypes.func.isRequired,
  skippedAddressStep: PropTypes.bool.isRequired,
  internetOption: PropTypes.oneOfType([PropTypes.bool, PropTypes.oneOf([null])]),
  onAddressSelect: PropTypes.func.isRequired,
  partner: PropTypes.object,
  analyticsLocation: PropTypes.string,
  isRequired: PropTypes.bool,
};

export default withTranslation()(React.memo(ZipCode));
